import { sub } from 'date-fns';
import { useContext, useState } from 'react';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import { MAX_DESTINATION_DELIVERY_TIMEOUT_SECONDS } from '../../../../../../../../domain/destination';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { numberWithCommas } from '../../../../../utils';
import { showChat } from '../../../../../utils/liveChat';
import { isFreePlan } from '../../../../../utils/subscription';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { formatChartRateNumber } from '../../../../common/Chart/format';
import LineChart from '../../../../common/Chart/LineChart';
import Switch from '../../../../common/Form/Fields/Switch';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { useAuthorized, usePermissionAwareDisabled } from '../../../../contexts/TeamPermissionContext';
import { ChartData, useMetric } from '../../Metrics/useMetric';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import { DashboardContext } from '../../DashboardContext';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import ChangeThrouhgputModal from './ChangeThroughputModal';
const TeamQuotas: React.FC = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    team,
    organization,
    mutateTeam
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const [is_updating_notifications, setIsUpdatingNotifications] = useState(false);
  const can_update_notifications = useAuthorized('admin');
  const notification_switch_disabled = usePermissionAwareDisabled(is_updating_notifications, 'admin');
  const {
    query
  } = useSearchQuery<{
    view: 'quota.request' | 'throughput';
  }>();
  const [time_unit, setTimeUnit] = useLocalStorage('pref:quota:time_unit', '30d');
  const {
    refresh_key
  } = useRefreshKey();
  const metrics_now = new Date(refresh_key);
  const {
    data: subscription_details
  } = useSWR(team!.billing_meta.price_interval_id && APIMethodKeys.billing.getSubscriptionDetails(), () => HookdeckAPI.billing.getSubscriptionDetails());
  const throughput_price_interval = team!.billing_meta.price_interval_id ? subscription_details?.price_intervals.find(pi => pi.id === team?.billing_meta.price_interval_id) : null;
  const throughput_price = team!.billing_meta.price_interval_id ? throughput_price_interval && throughput_price_interval!.price!.unit_config!.unit_amount : 0;
  const {
    data: plans
  } = useSWR(APIMethodKeys.billing.getPlans(), () => HookdeckAPI.billing.getPlans());
  const events_limit = subscription?.plan === 'free' && plans?.find(p => p.external_plan_id === subscription!.plan)?.prices?.find(p_0 => p_0.name.toLowerCase() === 'events')?.package_with_allocation_config?.allocation;
  const discarded_requests_limit = subscription?.plan === 'free' && plans?.find(p_1 => p_1.external_plan_id === subscription!.plan)?.prices?.find(p_2 => p_2.name.toLowerCase() === 'discarded requests')?.package_with_allocation_config?.allocation;
  const is_free_plan = isFreePlan(subscription!.plan);
  const quota_sections = [{
    title: `Plan limits`,
    quotas: [{
      title: 'Events limit',
      desc: 'Maximum number of events across all projects that Hookdeck will process each month.',
      value: events_limit ? numberWithCommas(Number(events_limit)) : 'Unlimited'
    }, {
      title: 'Discarded requests limit',
      desc: 'Maximum number of discarded requests across all projects each month.',
      value: discarded_requests_limit ? numberWithCommas(Number(discarded_requests_limit)) : 'Unlimited'
    }],
    desc: is_free_plan ? 'Upgrade to increase your plan limits.' : undefined,
    action: is_free_plan ? <Button outline icon="upgrade" to="/settings/organization/plans?highlight=events">
          Upgrade plan
        </Button> : null
  }, {
    title: 'Platform limits',
    quotas: [{
      title: 'API rate limit',
      desc: 'Requests to the admin API in excess of this rate will receive an HTTP 429 error response.',
      value: `${subscription!.api_rate_limit} req/m`
    }, {
      title: 'Webhook request payload size',
      desc: 'Maximum payload size processed by Hookdeck.',
      value: `${subscription!.max_payload_size_mb} MiB`
    }, {
      title: 'Delivery timeout',
      desc: 'Configured per destination. This is the hard maximum for each delivery attempt.',
      value: `${MAX_DESTINATION_DELIVERY_TIMEOUT_SECONDS / 60} minutes`
    }],
    desc: 'Contact us to increase your API rate or payload size limits.',
    action: <Button outline icon="chat" onClick={showChat}>
          Contact us
        </Button>
  }];
  const chart_props = {
    time_options: {
      time_unit,
      time_units: [{
        key: '1h',
        label: 'Past 1 hour'
      }, {
        key: '6h',
        label: 'Past 6 hours'
      }, {
        key: '1d',
        label: 'Past 24 hours'
      }, {
        key: '7d',
        label: 'Past 7 days'
      }, {
        key: '14d',
        label: 'Past 14 days'
      }, {
        key: '30d',
        label: 'Past 30 days'
      }],
      onTimeUnitSelected: setTimeUnit
    }
  };
  const max_events = useMetric<ChartData>('chart:line', 'events_max_rate', refresh_key, {
    date_range: {
      min: sub(metrics_now, time_unit.indexOf('h') > 0 ? {
        hours: Number(time_unit.replace('h', ''))
      } : {
        days: Number(time_unit.replace('d', ''))
      }).toISOString(),
      max: metrics_now.toISOString()
    }
  });
  let events_above_threshold = false;
  if (max_events.data && max_events.data.data) {
    const max_event = Math.max(...max_events.data.data.map(d => d.y as number));
    if (max_event > team!.max_events_per_second) {
      events_above_threshold = true;
    }
  }
  const handleThroughputNotificationsChange = async (enabled: boolean) => {
    setIsUpdatingNotifications(true);
    try {
      const updated_team = await HookdeckAPI.teams.update({
        notification_preferences: {
          ...team!.notification_preferences,
          project_throughput_exceed: enabled
        }
      });
      mutateTeam(updated_team);
      addToast('success', `Throughput notifications ${enabled ? 'enabled' : 'disabled'}.`);
    } catch {
      addToast('error', 'Failed to update throughput notifications.');
    } finally {
      setIsUpdatingNotifications(false);
    }
  };
  return <Container large left m={{
    b: 10
  }}>
      <Div m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        t: 0,
        b: 4
      }}>
          Project throughput
          <Text muted size="m">
            Maximum delivery attempts per second for each destination in this project.
          </Text>
        </Text>
        <StyledCard danger={events_above_threshold}>
          <StyledCardSection p={{
          y: 3,
          x: 4
        }} flex={{
          justify: 'space-between',
          align: 'center'
        }}>
            <Div>
              <Text size="xs" semi muted>
                Current throughput rate
              </Text>
              <Text semi flex>
                {team!.max_events_per_second} events per second
                {throughput_price !== undefined && <Text muted m={{
                l: 1
              }}>
                    / ${Number(throughput_price)} per month
                  </Text>}
              </Text>
            </Div>
            <Button disabled={isFreePlan(subscription!.plan) || !!subscription?.cancel_at || organization?.feature_flags.disable_throughput_change} outline icon="sort" to="/settings/project/quotas?view=throughput">
              Change throughput
            </Button>
          </StyledCardSection>
          {(isFreePlan(subscription!.plan) || subscription?.cancel_at) && <StyledCardSection p={{
          y: 3,
          x: 4
        }} flex={{
          justify: 'space-between'
        }}>
              <Div flex={{
            align: 'center'
          }}>
                <Icon muted icon="info" left />
                <Text>Upgrade your Organization’s plan to enable throughput modification.</Text>
              </Div>
              <Link to="/settings/organization/plans?highlight=starter" icon="arrow_forward">
                Upgrade plan
              </Link>
            </StyledCardSection>}
          {organization!.feature_flags?.disable_throughput_change && <StyledCardSection p={{
          y: 3,
          x: 4
        }} flex={{
          justify: 'space-between'
        }}>
              <Div flex={{
            align: 'center'
          }}>
                <Icon muted icon="info" left />
                <Text>Contact us to change your project throughput.</Text>
              </Div>
              <Link onClick={showChat} icon="arrow_forward">
                Contact us
              </Link>
            </StyledCardSection>}
          {events_above_threshold && <StyledCardSection p={{
          y: 3,
          x: 4
        }} danger flex={{
          align: 'flex-start',
          gap: 3
        }}>
              <Icon icon="error" danger m={0.5} />
              <Text>
                Delivery attempts exceed your project throughput per destination, increasing
                delivery latency. Inbound requests are still accepted.
              </Text>
            </StyledCardSection>}
        </StyledCard>
        <StyledCard m={{
        t: 6
      }}>
          <LineChart title="Throughput over time" legend_placeholder={'Max new events per second'} height={240} {...chart_props} loading={max_events.is_loading} datasets={max_events && max_events.data ? [{
          key: 'delivered',
          label: 'Max new events per second',
          theme_color: 'primary' as const,
          mono: false,
          data: max_events.data.data,
          total: max_events.data.total,
          highest: max_events.data.highest,
          average: max_events.data.average,
          getDataLabel: (v: number) => `${formatChartRateNumber(v, 'second')} / second`,
          metric_type: 'max' as const
        }, {
          key: 'limit',
          label: 'Throughput limit',
          theme_color: 'danger' as const,
          mono: false,
          dashed: true,
          data: max_events.data.data.map(d_0 => ({
            ...d_0,
            y: team!.max_events_per_second
          })),
          total: team!.max_events_per_second * max_events.data.data.length,
          highest: team!.max_events_per_second,
          average: team!.max_events_per_second,
          getDataLabel: (v_0: number) => `${formatChartRateNumber(v_0, 'second')} / second`,
          metric_type: 'rate' as const
        }] : []} />
        </StyledCard>
        <StyledCard m={{
        t: 6
      }}>
          <StyledCardSection p={{
          y: 3,
          x: 4
        }} flex={{
          justify: 'flex-start',
          align: 'center',
          gap: 3
        }}>
            <Tooltip tooltip="Only project admins can toggle throughput email notifications." disabled={can_update_notifications}>
              <Switch checked={team!.notification_preferences.project_throughput_exceed !== false} disabled={notification_switch_disabled} onChange={event => handleThroughputNotificationsChange(event.target.checked)} />
            </Tooltip>
            <Div flex={{
            align: 'center',
            gap: 1
          }}>
              <Text>Throughput email notifications</Text>
              <Tooltip tooltip="Send email notifications when delivery attempts exceed this project's throughput.">
                <Icon muted icon="info" />
              </Tooltip>
            </Div>
          </StyledCardSection>
        </StyledCard>
      </Div>
      {query.view === 'throughput' && <ChangeThrouhgputModal />}
      {quota_sections.map(section => <Div m={{
      b: 10
    }} key={section.title}>
          <Div m={{
        b: 4
      }} flex={{
        justify: 'space-between',
        align: 'center'
      }}>
            <Div>
              <Text bold size="l">
                {section.title}
              </Text>
              <Text muted>{section.desc}</Text>
            </Div>
            {section.action}
          </Div>
          <StyledCard m={{
        b: 4
      }}>
            <StyledCardSection muted>
              <Grid>
                <GridUnit size={3 / 4}>
                  <Div flex={{
                justify: 'space-between'
              }}>
                    <Text muted semi size="xs" p={{
                  x: 4,
                  y: 1
                }}>
                      Type
                    </Text>
                    <Divider vertical />
                  </Div>
                </GridUnit>
                <GridUnit size={1 / 4}>
                  <Text muted semi size="xs" p={{
                x: 4,
                y: 1
              }}>
                    Rate
                  </Text>
                </GridUnit>
              </Grid>
            </StyledCardSection>
            {section.quotas.map((quota, i) => <StyledCardSection key={i}>
                <Grid>
                  <GridUnit size={3 / 4}>
                    <Div flex={{
                justify: 'space-between'
              }}>
                      <Div p={{
                  x: 4,
                  y: 3
                }}>
                        <Text semi size="s">
                          {quota.title}
                        </Text>
                        <Text muted size="s">
                          {quota.desc}
                        </Text>
                      </Div>
                      <Divider vertical />
                    </Div>
                  </GridUnit>
                  <GridUnit size={1 / 4}>
                    <Div p={{
                x: 4,
                y: 3
              }}>
                      <Badge neutral>{quota.value}</Badge>
                    </Div>
                  </GridUnit>
                </Grid>
              </StyledCardSection>)}
          </StyledCard>
        </Div>)}
    </Container>;
};
export default TeamQuotas;